import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { UserActions } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { mergeMap, map, startWith, finalize, filter } from 'rxjs/operators';
import { SiteState } from 'src/app/shared/models';
import { SoilTypeService } from 'src/app/shared/services/soil-type.service';

import {
  SoilTypeActionTypes,
  LoadSoilTypesSuccess,
  LoadSoilTypesRequest,
} from '../actions/soil-type.actions';

@Injectable()
export class SoilTypeEffects {
  private readonly actions$ = inject(Actions);
  private readonly soilTypeService = inject(SoilTypeService);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly platform = inject<Object>(PLATFORM_ID);

  /**
   * userInitializedBefore is used to execute actions on the browser side during startup.
   */
  userInitializedBefore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.userInitializedBefore),
      map((_) => new LoadSoilTypesRequest()),
    ),
  );
  /**
   * userInitialized+isPlatformBrowser does the same as above, but also works for newly
   * initialized sessions when users log in on this site.
   */
  userInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.userInitialized),
      filter(() => isPlatformBrowser(this.platform)),
      map((_) => new LoadSoilTypesRequest()),
    ),
  );

  loadSoilTypes$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(SoilTypeActionTypes.LoadSoilTypesRequest),
      mergeMap(() =>
        this.soilTypeService.getSoilTypes().pipe(
          map((soilTypes) => new LoadSoilTypesSuccess(soilTypes)),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );
}
